/**
 * The German vocabulary of the **administration** screens — Admin → Benutzer, Owner → Companies / Shards /
 * Users / Integrationen / Plattform, the first-run Setup screen, and the company integration settings. (The
 * owner shell still labels most of its own screens in English; the two #608 screens are German. The sentences
 * below quote whatever each screen actually renders, per the glossary rule, rather than translating a label.)
 *
 * These screens used to render their refusals through `ErrorBannerComponent`, which showed
 * `Error · {{ error.statusText }}` and **discarded** both `code` and `description`. A named refusal therefore
 * reached the operator as `Error · Bad Request`: `E_USER_NAME_RESERVED_PREFIX` — the code ADR #469 says the UI
 * must branch on — arrived with nothing in it at all, and the locally-detected refusal the end-user
 * documentation quotes ("Passwords are not matching!") was English on a German product. The component is gone;
 * every one of those sites now posts a sentence from this table through `GlobalAlertService`.
 *
 * Admin → **Rollen** was one of these screens and has been retired (#279): it administered legacy
 * `Role`/`Permission` rows that no grant-based account can hold, and nothing in the app was reachable only
 * through them. Its locally-detected sentence (`NO_ROLE_SELECTED`) went with it.
 *
 * The two rules from the style guide's *"Surfacing an API error"* section hold here as everywhere:
 *
 * 1. **Never render the backend's `description`.** It is English, developer-facing, and free to be reworded by
 *    `ErrorCatalog` at any time.
 * 2. **A code with no sentence falls back to German**, never to the raw field.
 *
 * A third rule is specific to these screens and worth stating, because it decides how much of this file is
 * table and how much is fallback: **almost every failure here carries no `code` at all.**
 * `ResponseModelExtensions.BuildResponse` only emits the structured `ApiError` when `ErrorCode is not null`,
 * and the Management/Administration services predate the catalog — `CompanyService`, `ShardService` and the
 * three `AuthUnitService`s register **zero** codes between them. A `DynamicAuthorize`
 * refusal is a bare `ForbidResult`, a `[StringNotEmpty]` violation is a bare 400, `SettingController` answers a
 * hardcoded `BadRequest()` that never reaches `BuildResponse()` at all. So the **per-call-site fallback is the
 * whole message on most of these paths**, by construction rather than by omission — pass a specific one, never
 * lean on {@link ADMINISTRATION_ERROR_FALLBACK}.
 *
 * The places that rule does **not** hold are the fiscal chain and the two platform-settings screens: the
 * fiskaly onboarding chain has always carried a catalog (`E_LEGAL_MANAGING_ORGANIZATION_NOT_CONFIGURED`,
 * raised on Owner → Shards), and #608 gave Owner → **Integrationen** and Owner → **Plattform** three more
 * (`E_LEGAL_MANAGING_ORGANIZATION_ID_BLANK`, `E_TELEGRAM_BOT_TOKEN_BLANK`, `E_PLATFORM_DOMAIN_INVALID`), and
 * #629 gave the provider switch on **Rechtliches → Einstellungen** two more
 * (`E_LEGAL_PROVIDER_DEVICES_REGISTERED`, `E_LEGAL_PROVIDER_CASH_POINT_OPEN`), and #637 gave the alias DNS
 * automation eight (`E_DNS_*` plus `E_COMPANY_ALIAS_INVALID_LABEL`), #926 gave the demo company's key rules
 * two (`E_LEGAL_DEMO_*`), #933 gave the shared demo organisations eight more, and #921 two for a demo company's
 * tax-office report (`E_LEGAL_DEMO_SUBMISSION_*`). Those twenty-six are the
 * exception, not a sign the rest have caught up. Everything else
 * on these screens is still a bare status.
 *
 * One of the #637 codes does **not** arrive as a failure at all. A DNS problem never blocks an alias save, so
 * `PUT /Management/Company/SetAlias` answers `200` carrying a `dnsWarning` — an `ApiError`-shaped object on a
 * successful response. {@link describeAdministrationCode} is the entry point for that case: same table, same
 * rules, a bare `code` instead of an rxjs error.
 *
 * There used to be a **second** such button, on Owner → Companies, with a code and a sentence of its own
 * (`E_LEGAL_PLATFORM_COMPANY_NOT_DESIGNATED`). Both are gone: the endpoint behind it was the only path in the
 * product that could create a *top-level* fiskaly organisation — the platform's own managing organisation, of
 * which an installation has exactly one and which the operator creates in the fiskaly dashboard. A sentence for
 * a refusal the server can no longer answer is dead weight that reads as live vocabulary, so it went with it.
 *
 * Register is the formal *Sie* throughout (`npm run lint:register`), and each sentence is kept to the
 * instructional shape the working agreement asks for: short, active, present tense, one instruction per
 * sentence, naming the next action rather than restating an error id.
 */
import {readErrorCode} from '../../_auth/two-factor/second-factor-errors';

/**
 * Password and its confirmation do not match — checked in the browser, before anything is sent.
 *
 * Exported rather than written at each call site because **three** screens make the same check (Admin →
 * Benutzer, Owner → Benutzer, Setup) and the end-user documentation quotes the sentence verbatim
 * (`StaticPages/docs/.../personal/benutzer-rollen.mdx`). Three copies is three chances for the docs to describe
 * a wording no screen still uses.
 */
export const PASSWORD_CONFIRMATION_MISMATCH =
  'Passwort und Wiederholung stimmen nicht überein. Bitte geben Sie beide Felder erneut ein.';

/**
 * `DeviceCredentialErrorCodes.ReservedUsernamePrefix` (`E_USER_NAME_RESERVED_PREFIX`, 400) — the name trespasses
 * on the `cqme-` namespace that belongs to register credentials.
 *
 * Exported rather than left inline in {@link ADMINISTRATION_ERRORS} so the rule has **one sentence however many
 * doors** answer it: both Benutzer screens (`owner/users`, `admin/users`) create through `UserService.AddOnShard`
 * / `UserService.Add` and render this refusal from here. (The register screens once had a third door — a
 * "Konto für Kassen anlegen" dialog posting to `POST /device/enrollment/accounts` — which #279 retired along
 * with the anchor account it created; the export stays, because one refusal reported in two wordings is how a
 * reader concludes two screens have two different rules.)
 *
 * It names the prefix, because that is the only part of the input the reader has to change.
 */
export const RESERVED_USERNAME_PREFIX_MESSAGE =
  'Benutzernamen dürfen nicht mit „cqme-" beginnen. Dieses Präfix gehört zu den Kassen-Zugangsdaten. '
  + 'Bitte wählen Sie einen anderen Namen.';

/**
 * The German sentence for a structured refusal, keyed on `ApiError.code`.
 *
 * Twenty-two entries. Twenty-one of them are the true size of the structured surface across these screens — see
 * the file header; one is kept for a retired screen's code, for the reason stated at that entry. Sourced from
 * each code's C# XML doc rather than from its `ErrorCatalog` description, per the style guide.
 */
const ADMINISTRATION_ERRORS: Record<string, string> = {
  // LegalOnboardingErrorCodes.ManagingOrganizationNotConfigured (400), raised by
  // `IManagingOrganizationService.GetOrganizationId()` BEFORE the first fiskaly round-trip, and reaching this
  // screen through `OrganizationProcessor.RegisterShard` → `FiskalyService.UpsertShard` →
  // `POST /Management/Fiskaly/UpsertShard/{id}` — the „fiskaly registrieren“ action on Owner → Shards. (Both
  // that screen's heading and its navigation entry read "Shards", in English, like the rest of the owner shell.)
  //
  // On a fresh installation this refuses EVERY shard registration, permanently, until a platform owner records
  // the id: the managing organization used to be derived from the single `Company.Protected` row, whose only
  // writer (the first-run setup wizard) is gone, so nothing sets it implicitly any more. That makes this the
  // one code on these screens where the sentence has to be a procedure rather than a diagnosis — a retry can
  // never change the answer, and the reader is the only person who can.
  //
  // Names the exact FIELD, on the exact card, of the exact screen — a route, a card title and a label, all
  // quoted as they render. It used to name the raw key→value editor and the storage key
  // (`fiskaly_managing_organization_id`), because that editor was the only way to record the id and the
  // operator had to type the key as well as the value. #608 gave the id a typed field and retired the editor,
  // so the key is no longer anything the reader types — repeating it now would send them looking for a screen
  // that does not exist.
  //
  // It is still the ONLY structured fiskaly onboarding code raised on these screens, and Owner → Shards is the
  // only screen that can provoke it. The sibling that used to sit beneath this entry
  // (`E_LEGAL_PLATFORM_COMPANY_NOT_DESIGNATED`, from the Owner → Companies button) is gone with its endpoint.
  //
  // Quoted verbatim by `docs/articles/platform/fiskaly-managing-organization.md`,
  // `docs/articles/platform/platform-owner-settings.md` and the fiscal test plan, so the wording is a
  // contract: the path „Integrationen" → „Fiskaly" → field „Organisations-ID" is what those pages tell an
  // operator to follow, and a sentence that drifts from them sends somebody looking for a screen that is
  // spelled differently.
  E_LEGAL_MANAGING_ORGANIZATION_NOT_CONFIGURED:
    'Die Fiskaly-Organisations-ID der Plattform fehlt. Sie finden die ID im Fiskaly-Dashboard. '
    + 'Tragen Sie die ID unter „Integrationen" → „Fiskaly" im Feld „Organisations-ID" ein.',

  // LegalOnboardingErrorCodes.ManagingOrganizationIdBlank (400), from
  // `IManagingOrganizationService.SetOrganizationId` — the save button of that very field (#608). The reader is
  // already looking at the field, so the sentence says what to put in it rather than where it lives.
  E_LEGAL_MANAGING_ORGANIZATION_ID_BLANK:
    'Bitte geben Sie eine Organisations-ID ein. Sie finden die ID im Fiskaly-Dashboard.',

  // TelegramErrorCodes.BotTokenBlank (400), from the Telegram card's save button on Owner → Integrationen
  // (#608). Names BotFather because that is the only place a token comes from, and a reader who has none has
  // nowhere else to look.
  E_TELEGRAM_BOT_TOKEN_BLANK:
    'Bitte geben Sie ein Bot-Token ein. Sie erhalten es in Telegram von @BotFather.',

  // PlatformSettingErrorCodes.DomainInvalid (400), from the „Öffentliche Adresse" card on Owner → Plattform
  // (#608). The rule is narrower than "a valid URL" — the value must be scheme plus host and nothing else,
  // because its one reader appends `/static/privacy.html` to it — so the sentence states the shape and shows
  // it, which is the only form of this refusal a reader can act on without guessing.
  E_PLATFORM_DOMAIN_INVALID:
    'Bitte geben Sie die Adresse als vollständige Web-Adresse ohne Pfad an, z. B. https://app.cheqme.de.',

  // Answered by BOTH user-creation endpoints (`UserService.AddOnShard` for Admin → Benutzer, `UserService.Add`
  // for Owner → Benutzer), and answered BEFORE the duplicate-name lookup — so a `cqme-` name that also already
  // exists reports this, never the 409. The sentence is shared with the register screens; see the constant.
  E_USER_NAME_RESERVED_PREFIX: RESERVED_USERNAME_PREFIX_MESSAGE,

  // AuthErrorCodes.UserNameInvalid (400), from `UserService.AddOnShard`. It exists because `User`'s constructor
  // answers the same question by THROWING, which renders as a bare 500 — so the two creation screens used to
  // report "a space in the name" as an outage. Names both forbidden characters: the reader cannot see which of
  // the two they typed, and a space is invisible at the end of a field.
  E_USER_NAME_INVALID:
    'Der Benutzername darf kein Leerzeichen und kein „@" enthalten. Bitte wählen Sie einen anderen Namen.',

  // SessionScopeErrorCodes.ShardRequired (400), raised by `BaseController.RequireShard`. Reachable on all three
  // Admin → Benutzer calls from a company-only session (a CompanyOwner or Payroll context that selected the
  // company rather than one of its locations). The remedy is a context switch, not a retry, so the sentence
  // says so — "erneut versuchen" is the one thing that can never work here.
  E_SCOPE_SHARD_REQUIRED:
    'Dieser Bildschirm gilt für einen einzelnen Standort. Ihre Sitzung hat keinen Standort gewählt. '
    + 'Bitte wechseln Sie in einen Standort.',

  // AuthErrorCodes.PermissionGrantAboveCallerLevel (403), from `RoleService.ToggleRolePermission`. It covers
  // REMOVAL as well as granting, so the sentence says "vergeben oder entziehen" rather than only "vergeben" —
  // an administrator who reads "may not grant" and then cannot un-tick the box either learns nothing.
  // Deliberately names no retry: there is no action open to this reader, only a different person.
  //
  // NO SPA CALLER since the Rollen screen was retired (#279) — nothing here reaches
  // `Administration/Role/TogglePermission` any more. Kept anyway, and deliberately: the SERVER still registers
  // and answers the code, retiring the legacy permission system is its own work (#264), and a translation
  // removed now is a code that comes back untranslated. It goes when that system goes.
  E_PERMISSION_GRANT_ABOVE_CALLER_LEVEL:
    'Diese Berechtigung verwaltet die gesamte Installation. '
    + 'Nur der Plattformbetreiber kann sie vergeben oder entziehen.',

  // DeviceCredentialErrorCodes.NotADeviceLogin (400), from `UserService.Login`. Its own sentence rather than
  // the one `device-credential.models.ts` uses: there the reader typed a register credential into the wrong
  // form and the remedy is "use the till"; here an ADMINISTRATOR-created account was *named* `cqme-…`, and the
  // account exists but can never sign in — no button on the screen fixes it, so the sentence names the
  // platform operator instead of offering a remedy the reader does not have.
  //
  // The first-run setup wizard used to be the way to reach this state on an account nobody could then repair,
  // because it created the first user through the unguarded `Add(User)` overload. That path is gone; the
  // remaining creation paths (`AddOnShard`, `Add(NewUser)`) both check the reserved prefix, so this is now the
  // legacy-data case rather than a state a fresh installation can still be talked into.
  E_DEVICE_CREDENTIAL_NOT_A_DEVICE_LOGIN:
    'Der Benutzername ist für Kassen reserviert. Ein Name mit „cqme-" kann sich in der Verwaltung nicht '
    + 'anmelden. Bitte wenden Sie sich an den Plattformbetreiber.',

  // --- the legal provider switch, „Rechtliches" → „Einstellungen" (#629), and the credential save on the same
  // screen (#896) ---
  //
  // `LegalSettingsErrorCodes` (both 409), from `AuthenticationSettingsService`. Two guards, two codes, two
  // remedies — and until #629 neither code existed at all: both refusals answered with a bare `ResponseErrors`
  // ordinal, so `BuildResponse()` emitted no `ApiError` and the screen showed the same
  // „Provider konnte nicht ausgewählt werden." for a registered device, an open till, a network failure and a
  // 500. The whole point of the split is that the operator learns WHICH state to go and undo, so each sentence
  // names one action and neither offers a retry: nothing about repeating the request can change either answer.
  //
  // #896 put the same two guards in front of `SetCredentials` (`POST /Legal/Settings/SetCredentials`): renewing
  // „Key"/„Secret" of the provider already in force is guarded exactly like switching to a different one, and
  // answers with the SAME two codes. The sentence therefore names „Anbieter und Zugangsdaten" rather than only
  // the switch, so it reads correctly from either screen action.
  //
  // „Standort" rather than „Shard": that is the word the German surfaces use for a location throughout
  // (`StaticPages/docs/GLOSSARY.md`), and the reader of this screen is standing in one.
  E_LEGAL_PROVIDER_DEVICES_REGISTERED:
    'Der Anbieter und seine Zugangsdaten können nicht geändert werden, solange an diesem Standort Geräte '
    + 'registriert sind. Entfernen Sie zuerst alle Geräte.',

  // The device guard runs first, so this is only ever reached on a shard with no devices left — which is why it
  // can name the cash point as the single remaining obstacle rather than hedging.
  E_LEGAL_PROVIDER_CASH_POINT_OPEN:
    'Der Anbieter und seine Zugangsdaten können nicht geändert werden, solange an diesem Standort ein '
    + 'Kassenabschluss offen ist. Schließen Sie zuerst alle offenen Kassenpunkte.',

  // --- a demo company's keys (#926) ---
  //
  // `DemoLegalErrorCodes`. A Demo-Mandant (#916) may only hold keys from the provider's TEST environment, because
  // its bookings are fictional. Both answer the „Zugangsdaten" save on „Rechtliches" → „Einstellungen" and the
  // demo organisation form on Owner → „Integrationen" (#933). Two remedies, so two sentences: use a test key, or
  // wait for the provider. Neither names the supplier: the legal settings screen is a customer surface (#241).
  //
  // 422: fiskaly accepted the key and reported it as a LIVE key.
  E_LEGAL_DEMO_REQUIRES_TEST_ENV:
    'Ein Demo-Mandant nimmt nur Schlüssel aus der Testumgebung des Anbieters an. Dieser Schlüssel gehört zur '
    + 'Live-Umgebung. Legen Sie einen Testschlüssel an und tragen Sie ihn hier ein.',

  // 424: the provider was unreachable, or did not say which environment the key belongs to. Never read as a
  // test key, so the save is refused; asking again once the provider answers can change the result, which is
  // why this one sentence does offer a retry.
  E_LEGAL_DEMO_TEST_ENV_UNCONFIRMED:
    'Der Anbieter hat nicht bestätigt, dass dieser Schlüssel zur Testumgebung gehört. Ein Demo-Mandant kann '
    + 'ihn deshalb nicht speichern. Versuchen Sie es erneut, sobald der Anbieter erreichbar ist.',

  // 422 (#933): a test key, but not the one the location's demo slot was given (or the location has no slot yet).
  // Typing a key cannot fix this, so the sentence names the one remedy: register the location again on the
  // platform's „Shards" screen, which mints the slot's key and stores it. It names the screen, not the button,
  // because the button carries the supplier's name and this sentence reaches the customer's settings screen.
  E_LEGAL_DEMO_KEY_NOT_PLACED:
    'Ein Demo-Standort nimmt nur den Schlüssel an, den die Plattform bei seiner Registrierung angelegt hat. '
    + 'Registrieren Sie den Standort stattdessen unter „Shards" neu.',

  // --- a demo company's tax-office report (#921) ---
  //
  // `DemoLegalErrorCodes` again. A Demo-Mandant never reports its registers to the tax office under § 146a Abs. 4
  // AO, so neither refusal can change and neither sentence offers a retry. Neither names the supplier: both reach
  // customer surfaces.
  //
  // 409 on „Rechtliches" → „Einstellungen": a real provider for the „Kassenmeldung" card. „Keiner" is the label of
  // the card's provider option.
  E_LEGAL_DEMO_SUBMISSION_PROVIDER_REFUSED:
    'Ein Demo-Mandant meldet keine Kassen an das Finanzamt. Für die Kassenmeldung bleibt deshalb „Keiner" '
    + 'eingestellt.',

  // 409 on the approval page a „Meldung nach § 146a Abs. 4 AO" mail links to: the report belongs to a demo
  // company. Its reader followed a mail link and may not know the company is a demo, so the sentence says it.
  E_LEGAL_DEMO_SUBMISSION_REFUSED:
    'Diese Meldung gehört zu einem Demo-Mandanten. Demo-Mandanten melden keine Kassen an das Finanzamt, '
    + 'deshalb kann sie weder bestätigt noch abgelehnt werden.',

  // --- the shared demo organisations, Owner → „Integrationen" → „Demo-Organisationen" and Owner → Shards (#933) ---
  //
  // Also `DemoLegalErrorCodes`. These only reach the platform owner's screens, which name Fiskaly openly (the
  // Integrationen card already says „Fiskaly-Dashboard"), so the sentences may point at the dashboard's test
  // view, the one place the remedy happens. Each names one next action; none offers a retry, because repeating
  // the request changes nothing until the operator acts.
  //
  // 409 on „fiskaly registrieren": the pool is empty.
  E_LEGAL_DEMO_ORGANIZATION_NONE:
    'Es ist noch keine Demo-Organisation eingerichtet. Fügen Sie unter „Integrationen" eine Demo-Organisation '
    + 'hinzu und registrieren Sie den Standort danach.',

  // 409 on „fiskaly registrieren": every organisation holds as many TSS as its capacity allows.
  E_LEGAL_DEMO_ORGANIZATIONS_FULL:
    'Alle Demo-Organisationen sind voll. Legen Sie im Fiskaly-Dashboard (Testansicht) eine neue Organisation an '
    + 'und fügen Sie sie unter „Integrationen" hinzu, oder entfernen Sie eine Demo, die Sie nicht mehr brauchen.',

  // 422: the key belongs to another organisation, or LIVE is enabled for the organisation.
  E_LEGAL_DEMO_ORGANIZATION_REJECTED:
    'Diese Organisation kann nicht für Demos verwendet werden: Der Schlüssel gehört zu einer anderen '
    + 'Organisation, oder für die Organisation ist die Live-Umgebung freigeschaltet. Legen Sie im '
    + 'Fiskaly-Dashboard (Testansicht) eine neue Organisation an und verwenden Sie einen Schlüssel von ihr.',

  // 400: a field of the „Demo-Organisation hinzufügen" form is empty.
  E_LEGAL_DEMO_ORGANIZATION_FIELDS_BLANK:
    'Bitte geben Sie die Organisations-ID, den Testschlüssel und das zugehörige Secret ein.',

  // 409: the organisation id is already in the pool.
  E_LEGAL_DEMO_ORGANIZATION_DUPLICATE:
    'Diese Organisation ist bereits als Demo-Organisation eingetragen.',

  // 409: a demo location still sits in the organisation.
  E_LEGAL_DEMO_ORGANIZATION_IN_USE:
    'In dieser Demo-Organisation liegen noch Demo-Standorte. Entfernen Sie zuerst diese Demos.',

  // 404: a stale list; the entry is already gone.
  E_LEGAL_DEMO_ORGANIZATION_NOT_FOUND:
    'Diese Demo-Organisation gibt es nicht mehr. Laden Sie die Seite neu.',

  // --- the alias DNS automation, Owner → „Integrationen" → „DNS" (#637) ---
  //
  // `DnsErrorCodes`, from `AliasDnsService`. Seven codes reach the operator through TWO different doors, and
  // the split is why they are worth telling apart: the six below are refusals of the **DNS card's own**
  // actions („Speichern", „Jetzt abgleichen"), while `E_DNS_RECORD_CONFLICT` normally arrives on the *company
  // alias* screen as a `dnsWarning` — the alias was saved and only its record was not. Every sentence
  // therefore names ONE next action, and none of them offers a retry: nothing about repeating the request
  // changes a rejected token, a missing record or a foreign record already holding the name.

  // The selected provider is not one this build knows. Only reachable from a stale tab or a hand-made request,
  // so the sentence names the two choices rather than diagnosing.
  E_DNS_PROVIDER_UNKNOWN:
    'Dieser DNS-Anbieter ist nicht verfügbar. Bitte wählen Sie „Kein Anbieter" oder „Cloudflare".',

  // Cloudflare was selected and no token is stored. Names BotFather's equivalent — the Cloudflare profile page
  // — because a reader without a token has nowhere else to look, and names the two scopes, because a token
  // minted without them is rejected by the very next call.
  E_DNS_PROVIDER_TOKEN_BLANK:
    'Bitte geben Sie ein API-Token ein. Sie erstellen es in Cloudflare unter „My Profile" → „API Tokens" '
    + 'mit den Rechten „Zone:Read" und „DNS:Edit".',

  // The provider refused the credentials (Cloudflare `GET /user/tokens/verify` answered 401/403). Nothing was
  // stored: the verification runs BEFORE the switch, so the installation is unchanged and the remedy is a new
  // token, not a retry.
  E_DNS_PROVIDER_CREDENTIALS_REJECTED:
    'Das API-Token wurde vom Anbieter abgelehnt. Es wurde nichts gespeichert. Prüfen Sie, ob das Token noch '
    + 'gültig ist und die Rechte „Zone:Read" und „DNS:Edit" besitzt.',

  // No A record for the instance's own host. Zone and Ziel-IP are MIRRORED from that record, so without it
  // there is nothing to copy — which is why this names „Öffentliche Adresse" on the sibling screen, the one
  // field that makes the whole card work.
  E_DNS_MAIN_RECORD_NOT_FOUND:
    'Für die eigene Adresse dieser Installation wurde kein A-Eintrag gefunden. Tragen Sie unter '
    + '„Plattform" → „Öffentliche Adresse" die Adresse dieser Installation ein und legen Sie beim Anbieter '
    + 'einen A-Eintrag dafür an.',

  // The host is not inside any zone the token can see — usually a token scoped to the wrong zone rather than a
  // missing zone, so the sentence names both possibilities in the order they occur.
  E_DNS_ZONE_NOT_FOUND:
    'Zu dieser Adresse wurde beim Anbieter keine Zone gefunden. Prüfen Sie, ob die Domain dort verwaltet wird '
    + 'und ob das Token Zugriff auf diese Zone hat.',

  // The catch-all provider failure (network, 5xx, an unmapped error). The one code here where a retry IS the
  // remedy, so it is the one sentence that offers one — „Jetzt abgleichen" repeats exactly this work.
  E_DNS_PROVIDER_FAILED:
    'Der DNS-Anbieter konnte nicht erreicht werden. Die Einträge bleiben vorgemerkt; versuchen Sie es später '
    + 'erneut oder klicken Sie auf „Jetzt abgleichen".',

  // A record with this name already exists at the provider with different content. CheqMe never overwrites a
  // record it did not create — three environments share one zone — so the operator has to decide, and the
  // sentence says that instead of promising a repair.
  E_DNS_RECORD_CONFLICT:
    'Beim Anbieter gibt es bereits einen abweichenden Eintrag für diesen Namen. CheqMe überschreibt fremde '
    + 'Einträge nicht. Entfernen Sie den Eintrag beim Anbieter oder wählen Sie einen anderen Alias.',

  // CompanyErrorCodes.AliasInvalidLabel (400), from `CompanyService.SetAlias` (#637). The alias became a DNS
  // label the moment it started naming a record, so the rule tightened. It states the rule in full — a reader
  // cannot see WHICH character was refused, and „ungültig" alone sends them guessing — and says that the
  // server lowercases, so an upper-case entry does not read as the cause.
  E_COMPANY_ALIAS_INVALID_LABEL:
    'Der Alias darf nur Kleinbuchstaben, Ziffern und Bindestriche enthalten, muss mit einem Buchstaben oder '
    + 'einer Ziffer beginnen und enden und höchstens 63 Zeichen lang sein. Großbuchstaben werden automatisch '
    + 'umgewandelt.',

  // CompanyErrorCodes.AliasReserved (409, #934), from `SetAlias` and the company upsert. „demo" names the demo
  // e-mail domain, so no company may take it. A company that already holds it keeps it.
  E_COMPANY_ALIAS_RESERVED:
    'Dieser Alias ist für die Plattform reserviert. Bitte wählen Sie einen anderen Alias.'
};

/**
 * Shown for a refusal this build has no sentence for, so no English ever reaches the screen.
 *
 * A last resort, not a default: every call site in these screens passes its own fallback, because on these
 * endpoints the fallback usually **is** the message (see the file header).
 */
export const ADMINISTRATION_ERROR_FALLBACK =
  'Die Aktion war nicht möglich. Bitte versuchen Sie es erneut.';

/**
 * The German sentence for a failed administration call.
 *
 * @param err      whatever rxjs handed the `error` callback.
 * @param fallback the sentence for a failure carrying no recognised code. **Always pass one** — most refusals
 *                 on these screens are a bare status, so this is the entire error copy rather than a
 *                 decoration.
 */
export function describeAdministrationError(err: unknown,
                                            fallback: string = ADMINISTRATION_ERROR_FALLBACK): string {
  const code = readErrorCode(err);
  return (code && ADMINISTRATION_ERRORS[code]) || fallback;
}

/**
 * The German sentence for a bare refusal **code**.
 *
 * The sibling of {@link describeAdministrationError} for the one shape rxjs never sees: a structured problem
 * reported on a **successful** response. `SetAlias` answers `200 { company, dnsWarning }` — the alias was
 * stored and its DNS record was not — so the code arrives as data rather than as an error, and routing it
 * through the error helper would mean wrapping it back up in a fake `HttpErrorResponse` just to unwrap it.
 * (That wrapping habit is exactly what ADR #469 removed from these screens.)
 *
 * @param code     the `ApiError.code`, or `null`/`undefined` when there is no structured reason.
 * @param fallback the sentence for a code this build has no translation for. **Always pass one.**
 */
export function describeAdministrationCode(code: string | null | undefined,
                                           fallback: string = ADMINISTRATION_ERROR_FALLBACK): string {
  return (code && ADMINISTRATION_ERRORS[code]) || fallback;
}
